import { createContext, useContext } from 'react';

/**
 * Default fallback organization (The Polished Version).
 * Used on the apex domain, vercel previews, and localhost.
 */
export const POLISHED_VERSION_ORG_ID = 'a90138ee-26a5-4570-97b0-5a1144b9ad85';

export interface ResolvedOrg {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
  tagline?: string | null;
  description?: string | null;
  coverImageUrl?: string | null;
}

export interface OrgContextValue {
  org: ResolvedOrg;
  orgId: string;
  realOrg: ResolvedOrg;
  loading: boolean;
  /** True when visiting the apex domain root (no tenant subdomain). */
  isMarketingSite: boolean;
  /** True when subdomain was provided but didn't match any org. */
  isUnknownSubdomain: boolean;
  isSuspended: boolean;
  /** Subdomain detected (if any), e.g. "acme" for acme.thepolishedversion.com */
  subdomain: string | null;
  isImpersonating: boolean;
  impersonationStartedAt: number | null;
  /** Server-authoritative expiry (ms epoch) for the active view session, if any. */
  impersonationExpiresAt: number | null;
  canWrite: boolean;
  /**
   * Start a server-backed tenant support VIEW session. A reason (>= 8 chars) is
   * required and recorded/audited server-side; the browser no longer asserts the
   * impersonation event itself. Rejects if the reason is missing or the server
   * declines.
   */
  startImpersonation: (orgId: string, reason: string, caseRef?: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

export const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within an OrgProvider');
  return ctx;
}
